import React, { useState, useEffect } from "react";
import { makeStyles, withStyles, styled } from "@material-ui/core/styles";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TablePagination from "@material-ui/core/TablePagination";
import TableRow from "@material-ui/core/TableRow";
import TableFooter from "@material-ui/core/TableFooter";
import Paper from "@material-ui/core/Paper";
import Checkbox from "@material-ui/core/Checkbox";
import Loader from "../Loader";
import EnhancedTableHead from "../StaticSortTable/EnhancedTableHead";
import TablePaginationActions from "./TablePaginationActions";

const useStyles = makeStyles((theme) => ({
  root: {
    width: "100%",
    display: "inline",
  },
  paper: {
    position: "relative",
    width: "100%",
    overflowX: "auto",
  },
  table: {
    minWidth: 750,
  },
  visuallyHidden: {
    border: 0,
    clip: "rect(0 0 0 0)",
    height: 1,
    margin: -1,
    overflow: "hidden",
    padding: 0,
    position: "absolute",
    top: 20,
    width: 1,
  },
  tableHead: {
    backgroundColor: "#ECF0F1",
  },
  headCell: {
    fontWeight: "bold",
    fontSize: "12px",
    width: "100%",
    textAlign: "center",
    lineHeight: "12px",
  },
  pagination: {
    fontSize: "10px",
  },
  tableRowSelected: {
    "&$selected, &$selected:hover": {
      backgroundColor: "#ECF0F1",
    },
  },
  tableCell: {
    fontSize: "12px",
    marginLeft: "100px",
  },
  selected: {},
}));

const TableGenerator = ({
  fields = [],
  data = [],
  handleSortChange = () => {},
  currentPage = 0,
  initialSort = "",
  backendPagination = false,
  disablePagination = false,
  dataCount,
  searchQuery = "",
  onPageChange = () => {},
  onChangeSelected = () => {},
  onRowPerPageChange = () => {},
  rowOnePage = 10,
  radio = false,
  size = "medium",
  sensorTable = false,
  isRowClickable = false,
  onRowClick = () => {},
  ...props
}) => {
  const classes = useStyles();
  const [order, setOrder] = useState("desc");
  const [orderBy, setOrderBy] = useState(initialSort);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(rowOnePage);

  const StyledTableRow = styled(TableRow)(({ theme }) => ({
    "&:nth-of-type(odd)": {
      backgroundColor: theme.palette.action.hover,
    },
    // hide last border
    "&:last-child td, &:last-child th": {
      border: 0,
    },
  }));
  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
    handleSortChange(`${isAsc ? "" : "-"}${property}`);
  };

  useEffect(() => {
    setPage(currentPage);
  }, [currentPage]);

  const handleClick = (event, name) => {
    const selectedIndex = props.selectedRecords.findIndex(
      (val) => val.id === name.id
    );
    let newSelected = [];

    if (radio) {
      if (selectedIndex) {
        newSelected = [name];
      } else {
        newSelected = [];
      }
    } else {
      if (selectedIndex === -1) {
        newSelected = newSelected.concat(props.selectedRecords, name);
      } else if (selectedIndex === 0) {
        newSelected = newSelected.concat(props.selectedRecords.slice(1));
      } else if (selectedIndex === props.selectedRecords.length - 1) {
        newSelected = newSelected.concat(props.selectedRecords.slice(0, -1));
      } else if (selectedIndex > 0) {
        newSelected = newSelected.concat(
          props.selectedRecords.slice(0, selectedIndex),
          props.selectedRecords.slice(selectedIndex + 1)
        );
      }
    }
    onChangeSelected(newSelected);
  };

  const handleChangePage = (event, newPage, towards) => {
    setPage(newPage);
    onPageChange(newPage, towards);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
    onRowPerPageChange(event.target.value);
  };

  const handleSelectAll = (selected) => {
    if (selected) {
      onChangeSelected(data);
    } else {
      onChangeSelected([]);
    }
  };

  const isSelected = (record) =>
    props.selectedRecords.findIndex((x) => x.id === record.id) !== -1;

  return (
    <div className={classes.root}>
      <Paper
        className={classes.paper}
        style={{ height: props.tableHeight ? props.tableHeight : undefined }}
      >
        <Loader visible={props.loader} />
        <Table
          className={classes.table}
          aria-labelledby="tableTitle"
          size={size}
          aria-label="enhanced table"
          style={{ minHeight: 122, height: props.tableHeight }}
        >
          <EnhancedTableHead
            classes={classes}
            numSelected={props.selectedRecords.length}
            order={order}
            orderBy={orderBy}
            onRequestSort={handleRequestSort}
            rowCount={data.length}
            fields={fields}
            data={data}
            showSelectAll={props.showSelectAll}
            selectedRecords={props.selectedRecords}
            onSelectAll={handleSelectAll}
            stickyTable={props.stickyTable}
            showCheckbox={{ visibility: sensorTable && "hidden" }}
          />
          <TableBody>
            {data
              .slice(
                backendPagination ? 0 : page * rowsPerPage,
                page * rowsPerPage + rowsPerPage
              )
              .map((record, index) => {
                const isItemSelected = isSelected(record);
                const labelId = `enhanced-table-checkbox-${index}`;

                return (
                  <StyledTableRow
                    hover
                    role="checkbox"
                    aria-checked={isItemSelected}
                    tabIndex={-1}
                    key={record.id}
                    classes={{ selected: classes.selected }}
                    className={classes.tableRowSelected}
                    selected={isItemSelected}
                    style={{ height: 53 }}
                  >
                    {!props.diableCheckbox ? (
                      <TableCell padding="checkbox">
                        <Checkbox
                          color="primary"
                          style={{ visibility: sensorTable && "hidden" }}
                          onClick={(event) => handleClick(event, record)}
                          checked={isItemSelected}
                          inputProps={{ "aria-labelledby": labelId }}
                        />
                      </TableCell>
                    ) : (
                      <TableCell colSpan={1} />
                    )}
                    {fields
                      .filter((x) => x.visible)
                      .map((field) => (
                        <TableCell
                          key={field.key}
                          className={classes.tableCell}
                          padding={field.padding ? "checkbox" : "default"}
                          align="left"
                          onClick={(event) => onRowClick(event, record)}
                          style={{
                            cursor: isRowClickable ? "pointer" : "default",
                          }}
                        >
                          {field.render && typeof field.render === "function"
                            ? field.render(record[field.key], record, index)
                            : typeof record[field.key] !== "boolean"
                            ? record[field.key] != null
                              ? record[field.key]
                              : "---"
                            : record[field.key]}
                        </TableCell>
                      ))}
                  </StyledTableRow>
                );
              })}
            {!props.loader && data.length === 0 && (
              <TableRow
                className="d-flex align-items-center justify-content-center"
                style={{
                  position: "absolute",
                  height: "20%",
                  width: "100%",
                }}
              >
                <TableCell align="center" colSpan={100}>
                  <strong>No records found</strong>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
          {!disablePagination && (
            <TableFooter className={props.stickyTable ? "sticky-footer" : ""}>
              <TableRow>
                <TablePagination
                  rowsPerPageOptions={[3, 5, 7, 8, 10, 25, 50, 100]}
                  colSpan={fields.length}
                  count={dataCount !== undefined ? dataCount : data.length}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  SelectProps={{
                    inputProps: { "aria-label": "rows per page" },
                  }}
                  onPageChange={handleChangePage}
                  onChangeRowsPerPage={handleChangeRowsPerPage}
                  ActionsComponent={(e) => TablePaginationActions(e, props)}
                  labelDisplayedRows={({ from, to, count }) =>
                    props?.minimal
                      ? ""
                      : `${from}-${to} of ${
                          count !== -1 ? count : `more than ${to}`
                        }`
                  }
                />
              </TableRow>
            </TableFooter>
          )}
        </Table>
      </Paper>
    </div>
  );
};

TableGenerator.defaultProps = {
  selectedRecords: [],
  showSelectAll: true,
  handleSortChange: () => {},
};

export default withStyles({}, { withTheme: true })(TableGenerator);
